import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, MapPinIcon, ClipboardListIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, MapIcon, LogOutIcon, XIcon, ShieldCheckIcon, BuildingIcon, SettingsIcon, ScrollTextIcon, ReceiptIcon, PackageIcon, BoxIcon, TruckIcon, CalendarDaysIcon, FileOutputIcon, RotateCcwIcon, TagIcon, PercentIcon, ClipboardPlusIcon, PhoneIcon, ChevronDownIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../types';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  mobileMode?: boolean;
}
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  permission: keyof Permissions | 'settings' | 'bitacora' | 'cuentas_cobrar' | 'pedidos' | 'inventario' | 'caja_vacia' | 'facturas_dejadas' | 'devoluciones' | 'ofertas' | 'comisiones' | 'conduces' | 'compras' | 'call_center';
}
// Group definitions
const navGroups = [{
  label: 'GESTIÓN',
  items: ['dashboard', 'visits', 'new-visit', 'planificacion']
}, {
  label: 'COMERCIAL',
  items: ['collections', 'sales', 'pedidos', 'ofertas', 'comisiones', 'call-center']
}, {
  label: 'OPERACIONES',
  items: ['cuentas-cobrar', 'conduces', 'facturas-dejadas', 'devoluciones', 'compras', 'inventario', 'caja-vacia']
}, {
  label: 'ANÁLISIS',
  items: ['clients', 'reports', 'map']
}, {
  label: 'SISTEMA',
  items: ['users', 'roles', 'bitacora']
}];
const navItems: NavItem[] = [{
  id: 'dashboard',
  label: 'Panel Principal',
  icon: <LayoutDashboardIcon className="w-5 h-5" data-id="element-161" data-mp="uh0ibdikq4kp" />,
  permission: 'dashboard'
}, {
  id: 'visits',
  label: 'Mis Visitas',
  icon: <ClipboardListIcon className="w-5 h-5" data-id="element-162" data-mp="r1fiu9m69dk2" />,
  permission: 'visits'
}, {
  id: 'new-visit',
  label: 'Nueva Visita',
  icon: <MapPinIcon className="w-5 h-5" data-id="element-163" data-mp="vyxtq5jr22up" />,
  permission: 'new-visit'
}, {
  id: 'planificacion',
  label: 'Planificación',
  icon: <CalendarDaysIcon className="w-5 h-5" data-id="element-164" data-mp="i2u28k09g8lx" />,
  permission: 'planificacion'
}, {
  id: 'collections',
  label: 'Cobros',
  icon: <DollarSignIcon className="w-5 h-5" data-id="element-165" data-mp="8a69q1gbcqt0" />,
  permission: 'collections'
}, {
  id: 'sales',
  label: 'Ventas',
  icon: <ShoppingCartIcon className="w-5 h-5" data-id="element-166" data-mp="d26ce1nrpxta" />,
  permission: 'sales'
}, {
  id: 'pedidos',
  label: 'Pedidos',
  icon: <TruckIcon className="w-5 h-5" data-id="element-167" data-mp="3uftd4t2lach5" />,
  permission: 'pedidos'
}, {
  id: 'ofertas',
  label: 'Ofertas e Impulsos',
  icon: <TagIcon className="w-5 h-5" data-id="element-168" data-mp="3lhgdb6fx8wzh" />,
  permission: 'ofertas'
}, {
  id: 'comisiones',
  label: 'Comisiones',
  icon: <PercentIcon className="w-5 h-5" data-id="element-169" data-mp="3qf9etm6a52tf" />,
  permission: 'comisiones'
}, {
  id: 'conduces',
  label: 'Conduces',
  icon: <TruckIcon className="w-5 h-5" data-id="element-170" data-mp="36urdt4lo6jt3" />,
  permission: 'conduces'
}, {
  id: 'call-center',
  label: 'Call Center',
  icon: <PhoneIcon className="w-5 h-5" data-id="element-171" data-mp="3fv7ds437f8t7" />,
  permission: 'call_center'
}, {
  id: 'cuentas-cobrar',
  label: 'Cuentas x Cobrar',
  icon: <ReceiptIcon className="w-5 h-5" data-id="element-172" data-mp="3bsj2lpr0ppt2" />,
  permission: 'cuentas_cobrar'
}, {
  id: 'facturas-dejadas',
  label: 'Facturas Depositadas',
  icon: <FileOutputIcon className="w-5 h-5" data-id="element-173" data-mp="3pr0orj19yu8v" />,
  permission: 'facturas_dejadas'
}, {
  id: 'devoluciones',
  label: 'Devoluciones',
  icon: <RotateCcwIcon className="w-5 h-5" data-id="element-174" data-mp="3cqzz8tfnk7k" />,
  permission: 'devoluciones'
}, {
  id: 'compras',
  label: 'Compras',
  icon: <ClipboardPlusIcon className="w-5 h-5" data-id="element-175" data-mp="3ukvf35bdy7gx" />,
  permission: 'compras'
}, {
  id: 'inventario',
  label: 'Inventario',
  icon: <PackageIcon className="w-5 h-5" data-id="element-176" data-mp="7fnp7idhaekh" />,
  permission: 'inventario'
}, {
  id: 'caja-vacia',
  label: 'Caja Vacía',
  icon: <BoxIcon className="w-5 h-5" data-id="element-177" data-mp="21ujejzmqblrl" />,
  permission: 'caja_vacia'
}, {
  id: 'clients',
  label: 'Farmacias',
  icon: <BuildingIcon className="w-5 h-5" data-id="element-178" data-mp="26t4bnq6oppbt" />,
  permission: 'clients'
}, {
  id: 'reports',
  label: 'Reportes',
  icon: <FileTextIcon className="w-5 h-5" data-id="element-179" data-mp="2bnrv3amtztjj" />,
  permission: 'reports'
}, {
  id: 'map',
  label: 'Mapa GPS',
  icon: <MapIcon className="w-5 h-5" data-id="element-180" data-mp="1ix020m12bl0f" />,
  permission: 'map'
}, {
  id: 'users',
  label: 'Usuarios',
  icon: <UsersIcon className="w-5 h-5" data-id="element-181" data-mp="1nv1jx3mr5eab" />,
  permission: 'users'
}, {
  id: 'roles',
  label: 'Roles y Permisos',
  icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-182" data-mp="1rwn1316bkeqb" />,
  permission: 'roles'
}, {
  id: 'bitacora',
  label: 'Bitácora',
  icon: <ScrollTextIcon className="w-5 h-5" data-id="element-183" data-mp="1wuevq2ycmey7" />,
  permission: 'bitacora'
}];
export function Sidebar({
  currentPage,
  onNavigate,
  isOpen,
  onToggle,
  mobileMode = false
}: SidebarProps) {
  const {
    user,
    logout
  } = useAuth();
  const filteredItems = navItems.filter(item => {
    if (!user) return false;
    if (item.permission === 'clients') {
      return user.rolNombre === 'Administrador' || user.permisos?.users === true || user.permisos?.clients === true;
    }
    if (item.permission === 'bitacora') {
      return user.rolNombre === 'Administrador' || user.permisos?.bitacora === true;
    }
    if (['cuentas_cobrar', 'pedidos', 'inventario', 'caja_vacia', 'facturas_dejadas', 'devoluciones', 'ofertas', 'comisiones', 'conduces', 'compras', 'planificacion', 'descuento_pronto_pago', 'sales', 'inventario_editar', 'pedidos_editar', 'call_center'].includes(item.permission)) {
      const permKey = item.permission as keyof Permissions;
      return user.rolNombre === 'Administrador' || user.permisos?.[permKey] === true;
    }
    return user.permisos[item.permission as keyof Permissions] === true;
  });
  // ── Mobile Bottom Sheet ──────────────────────────────────────────────
  if (mobileMode) {
    return <AnimatePresence data-id="element-184" data-mp="24gt96zxn4sik">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onToggle} data-id="element-185" data-mp="28h3m6wjuoj6x" />

            {/* Bottom Sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 300
        }} className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl" style={{
          maxHeight: '85vh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }} data-id="element-186" data-mp="2s7fguq3qgas1">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2" data-id="element-187" data-mp="pi5tqn17o5mc">
                <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-188" data-mp="pgl0d0qmusq2" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-189" data-mp="z8gz8e0jmmvc">
                <div className="flex items-center gap-3" data-id="element-190" data-mp="uhien28iv2ma">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm border border-slate-100 overflow-hidden p-1" data-id="element-191" data-mp="ufxi572nd9ls">
                    <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-192" data-mp="1u4n5jnlez9k" />
                  </div>
                  <div data-id="element-193" data-mp="3mcsn638svptq">
                    <p className="text-sm font-bold text-slate-900" data-id="element-194" data-mp="3mcakanrmudcu">Menú</p>
                    <p className="text-[11px] text-slate-500" data-id="element-195" data-mp="3rdgb2g1qfqjq">{user?.nombre}</p>
                  </div>
                </div>
                <button onClick={onToggle} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-colors" data-id="element-196" data-mp="3vc7cgzd8ducn">
                  <XIcon className="w-5 h-5" data-id="element-197" data-mp="900g1fu0kpj8" />
                </button>
              </div>

              {/* Nav items */}
              <div className="overflow-y-auto px-4 py-4 space-y-5" style={{
            maxHeight: 'calc(85vh - 140px)'
          }} data-id="element-198" data-mp="dzv318ao4uz0">
                {navGroups.map(group => {
              const groupItems = filteredItems.filter(item => group.items.includes(item.id));
              if (groupItems.length === 0) return null;
              return <div key={group.label} data-id="element-199" data-mp="1ygm4r6yru3xc">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-1" data-id="element-200" data-mp="1yhnqq6hbsatu">
                        {group.label}
                      </p>
                      <div className="grid grid-cols-3 gap-2" data-id="element-201" data-mp="2r3p0xsfz62kf">
                        {groupItems.map(item => {
                    const isActive = currentPage === item.id;
                    return <button key={item.id} onClick={() => onNavigate(item.id)} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-all min-h-[72px] justify-center ${isActive ? 'bg-[#2952C4] text-white shadow-md shadow-blue-900/20' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 active:bg-slate-200'}`} data-id="element-202" data-mp="2hcc9v3203gfg">
                              <span className={isActive ? 'text-white' : 'text-slate-500'} data-id="element-203" data-mp="3tg817c0me340">
                                {item.icon}
                              </span>
                              <span className="text-[11px] font-semibold leading-tight" data-id="element-204" data-mp="l5nbhtpfnb38">
                                {item.label}
                              </span>
                            </button>;
                  })}
                      </div>
                    </div>;
            })}
              </div>

              {/* Bottom actions */}
              <div className="border-t border-slate-100 px-4 py-3 flex gap-2" data-id="element-205" data-mp="13xzbwawj02aq">
                <button onClick={() => onNavigate('settings')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${currentPage === 'settings' ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-206" data-mp="zxfdl407usn0">
                  <SettingsIcon className="w-4 h-4" data-id="element-207" data-mp="19m2rad5nqse8" />
                  Configuración
                </button>
                <button onClick={logout} className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors" data-id="element-208" data-mp="ztx0h6r56h0m">
                  <LogOutIcon className="w-4 h-4" data-id="element-209" data-mp="qutjq9fg6dpa" />
                  Salir
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>;
  }
  // ── Desktop Sidebar (unchanged) ──────────────────────────────────────
  return <aside className="fixed top-0 left-0 z-50 h-screen w-64 flex flex-col lg:sticky lg:top-0 lg:z-auto lg:translate-x-0 shadow-2xl" style={{
    background: 'linear-gradient(180deg, #0d1332 0%, #111a4a 100%)'
  }} data-id="element-210" data-mp="lt45kkwxhjwz">
      {/* Logo area */}
      <div className="flex items-center justify-between h-20 px-5 border-b border-white/10 flex-shrink-0 bg-[#0d1332]/50 backdrop-blur-sm" data-id="element-211" data-mp="2er1548kopl2g">
        <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-212" data-mp="29t7xdb3qgj02">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/50 flex-shrink-0 overflow-hidden p-1.5" data-id="element-213" data-mp="32hnb7ub4ov9t">
            <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-214" data-mp="2xjby9apje4nd" />
          </div>
          <div className="min-w-0 text-left" data-id="element-215" data-mp="1h1oc7lnksuvf">
            <p className="text-white font-bold text-sm leading-tight truncate font-heading" data-id="element-216" data-mp="16zkm3rm3isf8">
              Miladys Rodríguez
            </p>
            <p className="text-[11px] font-medium truncate opacity-80" style={{
            color: '#aac1ef'
          }} data-id="element-217" data-mp="1bz6w7s6mnsjt">
              & Asociados
            </p>
          </div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto custom-scrollbar" data-id="element-218" data-mp="1pq28legc4rs5">
        {navGroups.map(group => {
        const groupItems = filteredItems.filter(item => group.items.includes(item.id));
        if (groupItems.length === 0) return null;
        return <div key={group.label} className="space-y-1" data-id="element-219" data-mp="24nm8i6ydjwrg">
              <div className="px-3 mb-2" data-id="element-220" data-mp="24l01q9jb5laa">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider opacity-70" data-id="element-221" data-mp="5aqls5q02v0e">
                  {group.label}
                </p>
              </div>
              {groupItems.map(item => {
            const isActive = currentPage === item.id;
            return <button key={item.id} onClick={() => onNavigate(item.id)} className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                      transition-all duration-200 relative group
                      ${isActive ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
                    `} style={isActive ? {
              background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
            } : {}} data-id="element-222" data-mp="1vrvnc8ejhvfr">
                    <span className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-223" data-mp="tcvai0nk6cmk">
                      {item.icon}
                    </span>
                    {item.label}
                    {isActive && <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-300 shadow-[0_0_8px_rgba(147,197,253,0.6)]" data-id="element-224" data-mp="2vzshzx7sojid" />}
                  </button>;
          })}
            </div>;
      })}
      </nav>

      {/* Bottom actions */}
      <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-1 bg-[#0d1332]/30 backdrop-blur-md" data-id="element-225" data-mp="3sq1j9n0dk3ac">
        <button onClick={() => onNavigate('settings')} className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
            transition-all duration-200 relative
            ${currentPage === 'settings' ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
          `} style={currentPage === 'settings' ? {
        background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
      } : {}} data-id="element-226" data-mp="3eqjq6vg1q3v6">
          <SettingsIcon className="w-5 h-5" data-id="element-227" data-mp="1fe89jv9mtsua" />
          Configuración
        </button>
        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:bg-rose-500/20 hover:text-rose-200 transition-all duration-200" data-id="element-228" data-mp="1agykigq5nj6z">
          <LogOutIcon className="w-5 h-5" data-id="element-229" data-mp="10gfx5bzdtkkb" />
          Cerrar Sesión
        </button>
      </div>
    </aside>;
}